import type { FC } from 'react';
import { Col, Row } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AvatarSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';

const SkeletonLine: FC<{ height?: string; width: string }> = ({ height = '0.75rem', width }) => (
    <ButtonSkeleton block size='small' style={{ display: 'block', height, width }} />
);

const LifecycleStageSkeleton: FC<{ description?: boolean }> = ({ description = false }) => {
    const { token } = useAntDesignTheme();

    return (
        <div
            style={{
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusSM,
                padding: '0.5rem 0.75rem',
            }}
        >
            <Row align='middle' gutter={[8, 0]} wrap={false}>
                <Col flex='none'>
                    <AvatarSkeleton shape='square' size={18} />
                </Col>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <SkeletonLine width='45%' />
                </Col>
                <Col flex='none'>
                    <SkeletonLine height='0.625rem' width='4.5rem' />
                </Col>
            </Row>
            {description ? (
                <div style={{ display: 'grid', gap: '0.375rem', marginTop: '0.5rem' }}>
                    <SkeletonLine height='0.625rem' width='92%' />
                    <SkeletonLine height='0.625rem' width='68%' />
                </div>
            ) : null}
        </div>
    );
};

export const LifecyclePreviewFooterSkeleton: FC = () => {
    const { token } = useAntDesignTheme();

    return (
        <Row
            align='middle'
            aria-hidden
            justify='space-between'
            style={{
                background: token.colorBgContainer,
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                minHeight: '2.25rem',
                padding: '0.5rem 0.75rem',
            }}
            wrap={false}
        >
            <Col flex='none'>
                <SkeletonLine height='0.625rem' width='6.5rem' />
            </Col>
            <Col flex='none'>
                <SkeletonLine height='0.625rem' width='7.5rem' />
            </Col>
        </Row>
    );
};

const LifecyclePreviewSkeleton: FC = () => {
    const { token } = useAntDesignTheme();

    return (
        <div aria-busy='true' aria-label='Loading lifecycle preview' role='status'>
            <div style={{ padding: '1rem' }}>
                <Row align='middle' gutter={[8, 0]} wrap={false}>
                    <Col flex='none'>
                        <AvatarSkeleton shape='square' size={42} />
                    </Col>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <div style={{ display: 'grid', gap: '0.375rem' }}>
                            <SkeletonLine height='0.875rem' width='58%' />
                            <SkeletonLine height='0.75rem' width='42%' />
                        </div>
                    </Col>
                    <Col flex='none'>
                        <ButtonSkeleton size='small' style={{ width: '6.5rem' }} />
                    </Col>
                </Row>

                <div
                    style={{
                        borderTop: `1px solid ${token.colorBorderSecondary}`,
                        display: 'grid',
                        gap: '0.375rem',
                        marginTop: '0.75rem',
                        paddingTop: '0.75rem',
                    }}
                >
                    <SkeletonLine width='100%' />
                    <SkeletonLine width='76%' />
                </div>

                <Row align='middle' justify='space-between' style={{ marginTop: '0.875rem' }} wrap={false}>
                    <Col flex='none'>
                        <SkeletonLine height='0.625rem' width='8.25rem' />
                    </Col>
                    <Col flex='none'>
                        <SkeletonLine height='0.625rem' width='8.25rem' />
                    </Col>
                </Row>
                <ButtonSkeleton
                    block
                    shape='default'
                    size='small'
                    style={{ display: 'block', height: '0.375rem', marginTop: '0.375rem', width: '100%' }}
                />
                <Row align='middle' justify='space-between' style={{ marginTop: '0.5rem' }} wrap={false}>
                    <Col flex='none'>
                        <SkeletonLine height='0.625rem' width='10.5rem' />
                    </Col>
                    <Col flex='none'>
                        <SkeletonLine height='0.625rem' width='4.75rem' />
                    </Col>
                </Row>
            </div>

            <div
                style={{
                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                    display: 'grid',
                    gap: '0.5rem',
                    padding: '0.75rem',
                }}
            >
                <Row align='middle' justify='space-between' style={{ padding: '0 0.25rem 0.25rem' }} wrap={false}>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <SkeletonLine height='0.625rem' width='8.5rem' />
                    </Col>
                    <Col flex='none'>
                        <span aria-hidden style={{ alignItems: 'center', display: 'flex', paddingLeft: '0.5rem' }}>
                            {Array.from({ length: 3 }, (_, index) => (
                                <AvatarSkeleton key={index} size={18} style={{ marginLeft: index ? '-0.375rem' : 0 }} />
                            ))}
                        </span>
                    </Col>
                </Row>
                <LifecycleStageSkeleton description />
                <LifecycleStageSkeleton />
                <LifecycleStageSkeleton />
            </div>
        </div>
    );
};

export default LifecyclePreviewSkeleton;
